import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { Referrer } from '../types';
import OptionRow from './OptionRow';
import {
  EmptyRow,
  LoadingRow,
  OptionList,
  PanelRoot,
  SelectAllRow,
} from './styles';

type P = {
  options: Referrer[];
  selected: Referrer[];
  /** 제거 불가(양식 고정) 참조인 id — 체크 해제 불가라 체크박스를 비활성화한다 */
  lockedIds?: ReadonlySet<string>;
  isLoading: boolean;
  /** 키보드 전용 하이라이트 인덱스 — 마우스는 CSS hover 로만 표시 */
  focusedIndex: number;
  onToggle: (r: Referrer) => void;
  allowSelectAll: boolean;
  onToggleAll: () => void;
  maxCount?: number;
  /** 검색어 — 옵션 이름에서 일치 부분 하이라이트용 */
  keyword?: string;
};

/**
 * 드롭다운 팝오버 내용물 — 전체선택 행 + 옵션 리스트.
 * focusedIndex: 전체선택 행 있으면 0=전체선택·1..N=options[i-1], 없으면 0..N-1 (상위 ReferrerField 와 일치).
 */
const DropdownPanel = ({
  options,
  selected,
  lockedIds,
  isLoading,
  focusedIndex,
  onToggle,
  allowSelectAll,
  onToggleAll,
  maxCount,
  keyword = '',
}: P) => {
  const { t } = useTranslation();
  const selectedIds = new Set(selected.map((s) => s.id));

  const hasSelectAllRow = allowSelectAll && options.length > 0;
  const optionFocusedIndex = hasSelectAllRow ? focusedIndex - 1 : focusedIndex;
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    if (optionFocusedIndex >= 0) {
      rowRefs.current[optionFocusedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [optionFocusedIndex]);

  const visibleOptionIds = options.map((o) => o.id);
  const allVisibleSelected =
    visibleOptionIds.length > 0 &&
    visibleOptionIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    visibleOptionIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  if (isLoading) {
    return (
      <PanelRoot role="listbox">
        <LoadingRow>
          <Spinner theme="black" size="20px" />
        </LoadingRow>
      </PanelRoot>
    );
  }

  if (options.length === 0) {
    return (
      <PanelRoot role="listbox">
        <EmptyRow>{t('검색 결과가 없어요.')}</EmptyRow>
      </PanelRoot>
    );
  }

  const selectAllFocused = hasSelectAllRow && focusedIndex === 0;

  return (
    <PanelRoot role="listbox">
      {hasSelectAllRow && (
        <SelectAllRow
          $focused={selectAllFocused}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onToggleAll}
        >
          <Checkbox
            sx={{ padding: 0 }}
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected}
            tabIndex={-1}
            inputProps={{ 'aria-label': t('전체 선택') }}
            onClick={(e) => e.stopPropagation()}
            onChange={onToggleAll}
          />
          <span>{t('전체선택')}</span>
        </SelectAllRow>
      )}
      <OptionList>
        {options.map((opt, i) => {
          const isSelected = selectedIds.has(opt.id);
          const isLocked = isSelected && (lockedIds?.has(opt.id) ?? false);
          const reachedCap =
            !isSelected &&
            typeof maxCount === 'number' &&
            selected.length >= maxCount;
          return (
            <OptionRow
              key={opt.id}
              referrer={opt}
              keyword={keyword}
              checked={isSelected}
              focused={i === optionFocusedIndex}
              disabled={reachedCap || isLocked}
              onToggle={onToggle}
              rootRef={(el) => {
                rowRefs.current[i] = el;
              }}
            />
          );
        })}
      </OptionList>
    </PanelRoot>
  );
};

export default DropdownPanel;
